import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { FlowFooter } from "@/components/consultation-flow/ui/FlowFooter";
import { AlertCircle, Edit2, Info } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type {
  DemographicConfiguration,
  PatientInput,
  Amendment,
  AmendmentReason,
  BannerFieldConfig,
  SensitiveAttributeField,
} from "@/lib/pharmacyFirst/types";
import {
  getSensitiveAttributeDisplayValue,
  isSensitiveAttributeField,
} from "./sensitiveAttributeHelpers";
import { DEFAULT_AMENDMENT_REASON_LABELS } from "@/lib/pharmacyFirst";
import { calculateAgeYearsFromDateOfBirth } from "@/lib/pharmacyFirst/utils/ageCalculator";

interface DemographicConfirmationBannerProps {
  configuration: DemographicConfiguration;
  patient: PatientInput;
  isDarkMode?: boolean;
  onConfirm: (
    amendments: Amendment[],
    extras: { consentObtained: boolean },
  ) => void;
  onCancel?: () => void;
  onConsentRefused?: () => void;
  /** Opt-in presentation: text change links, radio consent, plain-words purpose lines and the shared footer. */
  refined?: boolean;
}

export function DemographicConfirmationBanner({
  configuration,
  patient,
  isDarkMode = false,
  onConfirm,
  onCancel,
  onConsentRefused,
  refined = false,
}: DemographicConfirmationBannerProps) {
  const [amendments, setAmendments] = useState<Amendment[]>([]);
  const [consentChoice, setConsentChoice] = useState<
    "obtained" | "refused" | null
  >(null);
  const [isDataSharingPopupOpen, setIsDataSharingPopupOpen] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<unknown>(null);
  const [editReason, setEditReason] = useState<AmendmentReason | "">("");
  const [editNote, setEditNote] = useState("");

  const formatSelectLikeValue = (
    value: unknown,
    fieldConfig?: BannerFieldConfig,
  ): string | null => {
    if (!fieldConfig?.selectOptions) return null;
    const match = fieldConfig.selectOptions.find(
      (opt) => opt.value === String(value),
    );
    return match?.label ?? null;
  };

  const getFieldValue = (field: BannerFieldConfig["field"]): unknown => {
    const amendment = amendments.find((a) => a.field === field);
    return amendment ? amendment.newValue : (patient as any)[field];
  };

  const formatFieldValue = (
    value: unknown,
    fieldConfig?: BannerFieldConfig,
  ): string => {
    if (value === undefined || value === null) return "—";
    if (fieldConfig?.field === "dateOfBirth") {
      if (typeof value === "string" && value.trim()) {
        const trimmed = value.trim();
        const ukMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (ukMatch) return trimmed;
        const parsed = new Date(trimmed);
        if (!Number.isNaN(parsed.getTime())) {
          const dd = String(parsed.getDate()).padStart(2, "0");
          const mm = String(parsed.getMonth() + 1).padStart(2, "0");
          const yyyy = parsed.getFullYear();
          return `${dd}/${mm}/${yyyy}`;
        }
      }
      return "—";
    }
    if (fieldConfig?.type === "sensitive_attribute") {
      const unwrappedValue = getSensitiveAttributeDisplayValue(value);
      const selectLabel = formatSelectLikeValue(unwrappedValue, fieldConfig);
      if (selectLabel) return selectLabel;
      if (typeof unwrappedValue === "boolean") return unwrappedValue ? "Yes" : "No";
      if (typeof unwrappedValue === "string") return unwrappedValue;
      return String(unwrappedValue);
    }
    if (fieldConfig?.type === "select" && fieldConfig.selectOptions) {
      const match = fieldConfig.selectOptions.find(
        (opt) => opt.value === String(value),
      );
      if (match) return match.label;
    }
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "string") return value;
    if (Array.isArray(value))
      return value.length > 0
        ? value.map((v: any) => (v.substance ? v.substance : v)).join(", ")
        : "None known";
    return String(value);
  };

  const handleEdit = (fieldConfig: BannerFieldConfig) => {
    const currentValue = getFieldValue(fieldConfig.field);
    setEditingField(fieldConfig.field);
    if (fieldConfig.type === "sensitive_attribute") {
      setEditValue(getSensitiveAttributeDisplayValue(currentValue));
    } else if (Array.isArray(currentValue)) {
      setEditValue(
        currentValue
          .map((v: any) => (typeof v === "string" ? v : v?.substance || v?.name || ""))
          .filter(Boolean)
          .join(", "),
      );
    } else {
      setEditValue(currentValue);
    }
    setEditReason("");
    setEditNote("");
  };

  const handleSaveAmendment = () => {
    if (!editingField || !editReason) {
      alert("Please select a reason for the amendment");
      return;
    }

    const fieldConfig = configuration.bannerFields.find(
      (f) => f.field === editingField
    );
    if (!fieldConfig) return;

    const originalValue = (patient as any)[editingField];
    let savedValue = editValue;
    if (Array.isArray(originalValue) && typeof editValue === "string") {
      savedValue = editValue
        .split(",")
        .map((s: string) => s.trim())
        .filter(Boolean)
        .map((substance: string) => ({ substance }));
    }
    const newAmendment: Amendment = {
      field: editingField as Amendment["field"],
      originalValue:
        fieldConfig.type === "sensitive_attribute"
          ? getSensitiveAttributeDisplayValue(originalValue)
          : originalValue,
      newValue: savedValue,
      reason: editReason as AmendmentReason,
      reasonNote: editNote || undefined,
      amendedAt: new Date().toISOString(),
    };

    const newAmendments = amendments.filter((a) => a.field !== editingField);
    setAmendments([...newAmendments, newAmendment]);
    setEditingField(null);
  };

  const derivedAge = (): number | null =>
    calculateAgeYearsFromDateOfBirth(getFieldValue("dateOfBirth")) ??
    (typeof patient.ageYears === "number" ? patient.ageYears : null);

  const selectedOptionHelp = (fieldConfig: BannerFieldConfig, raw: unknown): string | undefined =>
    fieldConfig.selectOptions?.find((o) => o.value === String(getSensitiveAttributeDisplayValue(raw)))?.helpText;

  const whyWeAskVisible = (fieldConfig: BannerFieldConfig): boolean => {
    const w = fieldConfig.whyWeAskShowWhen;
    if (!fieldConfig.whyWeAsk) return false;
    if (!w) return true;
    const age = derivedAge();
    if (age === null) return false;
    return (w.patientAgeUnder === undefined || age < w.patientAgeUnder) &&
      (w.patientAgeAtLeast === undefined || age >= w.patientAgeAtLeast);
  };

  const isFieldMissing = (fieldConfig: BannerFieldConfig): boolean => {
    if (refined && fieldConfig.derivedFromDateOfBirth) return derivedAge() === null;
    const value = getFieldValue(fieldConfig.field);
    const fieldValue =
      fieldConfig.type === "sensitive_attribute"
        ? getSensitiveAttributeDisplayValue(value)
        : value;
    if (fieldValue === undefined || fieldValue === null) return true;
    if (typeof fieldValue === "string" && fieldValue.trim() === "") return true;
    return false;
  };

  const isFieldVisible = (fieldConfig: BannerFieldConfig): boolean => {
    const w = fieldConfig.showWhen;
    if (!w) return true;
    const age = patient.ageYears;
    if (typeof w.patientAgeUnder === "number") {
      if (age === undefined || age === null) return false;
      if (!(age < w.patientAgeUnder)) return false;
    }
    if (typeof w.patientAgeAtLeast === "number") {
      if (age === undefined || age === null) return false;
      if (!(age >= w.patientAgeAtLeast)) return false;
    }
    return true;
  };

  const visibleFields = configuration.bannerFields.filter(isFieldVisible);
  const missingFields = visibleFields.filter((f) => isFieldMissing(f));
  const hasMissing = missingFields.length > 0;

  const handleConfirm = () => {
    if (hasMissing) return;
    if (consentChoice !== "obtained") return;
    onConfirm(amendments, { consentObtained: true });
  };

  // Refined mode records the refusal on Cancel; the radio itself only selects.
  const handleRefinedCancel = () => {
    if (consentChoice === "refused") onConsentRefused?.();
    onCancel?.();
  };

  const refinedBlockedReason = hasMissing
    ? missingFields.length === 1
      ? `Confirm "${missingFields[0].label}" to continue.`
      : `Confirm ${missingFields.length} missing fields to continue.`
    : consentChoice === null
      ? "Record whether consent was obtained to continue"
      : "Consent refused — select Cancel to end the consultation.";

  const fieldConfigMap = new Map(
    configuration.bannerFields.map((f) => [f.field, f])
  );

  return (
    <>
      <div
        className={`border rounded-2xl p-6 ${
          isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
        }`}
      >
        <h2
          className={`text-lg font-semibold mb-1 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Confirm patient demographics
        </h2>
        <p
          className={`text-sm mb-6 ${
            isDarkMode ? "text-gray-400" : "text-gray-600"
          }`}
        >
          {refined
            ? "Check each detail with the patient before continuing. Use Change to amend any field that is out of date or incorrect — amendments are recorded in the consultation audit trail."
            : "Check each detail with the patient before continuing. Use the pencil to amend any field that is out of date or incorrect — amendments are recorded in the consultation audit trail."}
        </p>

        <div className="space-y-0">
          {visibleFields.map((fieldConfig, idx) => (
            <div
              key={fieldConfig.field}
              className={`flex items-center justify-between p-4 ${
                idx !== visibleFields.length - 1
                  ? `border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`
                  : ""
              }`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p
                    className={`text-sm font-medium ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {!refined && fieldConfig.field === "dateOfBirth"
                      ? "Date of Birth"
                      : fieldConfig.label}
                  </p>
                  {isFieldMissing(fieldConfig) && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                      Needs confirmation
                    </span>
                  )}
                </div>
                <p
                  className={`text-lg font-semibold mt-1 ${
                    isFieldMissing(fieldConfig)
                      ? "text-amber-600"
                      : isDarkMode
                        ? "text-white"
                        : "text-gray-900"
                  }`}
                >
                  {refined && fieldConfig.derivedFromDateOfBirth
                    ? `${derivedAge() ?? "—"} (${fieldConfig.derivedFromDateOfBirth.note})`
                    : formatFieldValue(
                        getFieldValue(fieldConfig.field),
                        fieldConfig,
                      )}
                </p>
                {refined && selectedOptionHelp(fieldConfig, getFieldValue(fieldConfig.field)) && (
                  <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {selectedOptionHelp(fieldConfig, getFieldValue(fieldConfig.field))}
                  </p>
                )}
                {refined && whyWeAskVisible(fieldConfig) && (
                  <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {fieldConfig.whyWeAsk}
                  </p>
                )}
                {!refined &&
                  fieldConfig.type === "sensitive_attribute" &&
                  isSensitiveAttributeField(getFieldValue(fieldConfig.field)) && (
                    <p
                      className={`text-xs mt-1 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Processed under: provision of healthcare. Purpose:{" "}
                      {
                        (getFieldValue(fieldConfig.field) as SensitiveAttributeField<unknown>)
                          .purposeOfProcessing
                      }
                    </p>
                  )}
              </div>
              {refined ? (
                fieldConfig.derivedFromDateOfBirth ? null : (
                  <button
                    type="button"
                    onClick={() => handleEdit(fieldConfig)}
                    className={`text-sm font-medium underline underline-offset-2 ${
                      isDarkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-700 hover:text-blue-900"
                    }`}
                  >
                    {fieldConfig.changeLinkLabel ?? `Change ${fieldConfig.label.toLowerCase()}`}
                  </button>
                )
              ) : (
                <button
                  onClick={() => handleEdit(fieldConfig)}
                  className={`p-2 rounded-lg transition ${
                    isDarkMode
                      ? "hover:bg-gray-700 text-gray-400 hover:text-white"
                      : "hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <Edit2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        {hasMissing && (
          <Alert
            variant="default"
            className="mt-6 border-amber-200 bg-amber-50 text-amber-900"
          >
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription>
              {missingFields.length === 1
                ? `Please confirm "${missingFields[0].label}" before continuing.`
                : `Please confirm ${missingFields.length} missing fields before continuing.`}
            </AlertDescription>
          </Alert>
        )}

        <div
          className={`mt-6 border rounded-xl p-4 ${
            isDarkMode ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-gray-50"
          }`}
        >
          <p
            className={`text-sm font-semibold mb-1 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Patient consent
          </p>
          <p
            className={`text-xs mb-3 ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Confirm informed consent has been obtained from the patient (or
            carer) before starting the consultation. Recorded in the audit
            trail.
          </p>
          <div className="flex justify-end mb-3">
            <button
              type="button"
              onClick={() => setIsDataSharingPopupOpen(true)}
              className={`flex items-center gap-1 text-xs font-medium hover:underline ${
                isDarkMode ? "text-blue-400" : "text-blue-600"
              }`}
            >
              <Info className="w-3 h-3" />
              View Data Sharing Details
            </button>
          </div>
          {refined ? (
            <RadioGroup
              value={consentChoice ?? ""}
              onValueChange={(v) => setConsentChoice(v as "obtained" | "refused")}
              aria-label="Patient consent"
              className="space-y-2"
            >
              {(
                [
                  { v: "obtained", label: "Consent obtained" },
                  { v: "refused", label: "Consent refused" },
                ] as const
              ).map((opt) => (
                <label
                  key={opt.v}
                  htmlFor={`demographics-consent-${opt.v}`}
                  className={`flex items-center gap-2 rounded-lg border p-3 cursor-pointer transition ${
                    consentChoice === opt.v
                      ? isDarkMode ? "border-blue-500 bg-blue-950" : "border-blue-500 bg-blue-50"
                      : isDarkMode ? "border-gray-600 hover:bg-gray-700" : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >
                  <RadioGroupItem value={opt.v} id={`demographics-consent-${opt.v}`} />
                  <span className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}>{opt.label}</span>
                </label>
              ))}
            </RadioGroup>
          ) : (
          <div className="grid grid-cols-2 gap-2">
            {(
              [
                { v: "obtained", label: "Consent obtained" },
                { v: "refused", label: "Consent refused" },
              ] as const
            ).map((opt) => {
              const selected = consentChoice === opt.v;
              const selectedCls =
                opt.v === "obtained"
                  ? isDarkMode
                    ? "border-emerald-400 bg-emerald-500 text-black shadow-sm"
                    : "border-emerald-600 bg-emerald-600 text-white shadow-sm"
                  : isDarkMode
                    ? "border-red-400 bg-red-500 text-black shadow-sm"
                    : "border-red-600 bg-red-600 text-white shadow-sm";
              const hoverUnselected =
                opt.v === "obtained"
                  ? isDarkMode
                    ? "hover:border-emerald-500 hover:bg-emerald-500/10 hover:text-emerald-200"
                    : "hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-800"
                  : isDarkMode
                    ? "hover:border-red-500 hover:bg-red-500/10 hover:text-red-200"
                    : "hover:border-red-400 hover:bg-red-50 hover:text-red-800";
              const unselectedCls = isDarkMode
                ? `border-gray-600 bg-gray-700 text-gray-200 ${hoverUnselected}`
                : `border-gray-300 bg-white text-gray-800 ${hoverUnselected}`;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => {
                    setConsentChoice(opt.v);
                    if (opt.v === "refused") {
                      onConsentRefused?.();
                    }
                  }}
                  aria-pressed={selected}
                  className={`px-4 py-2.5 rounded-lg border-2 text-sm font-semibold transition-all duration-150 select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
                    selected
                      ? `${selectedCls} hover:brightness-105`
                      : `${unselectedCls} hover:shadow-sm`
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          )}
          {refined && consentChoice === "refused" && (
            <p className={`mt-3 text-sm ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
              The consultation will end.
            </p>
          )}
          {!refined && consentChoice === "refused" && (
            <Alert className="mt-3 border-red-200 bg-red-50 text-red-900">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription>
                Consent refused — the consultation cannot proceed. Cancel and
                document the refusal.
              </AlertDescription>
            </Alert>
          )}
        </div>

        {refined ? (
          <FlowFooter
            fullBleed
            isDarkMode={isDarkMode}
            cancel={onCancel ? { label: "Cancel", onClick: handleRefinedCancel, testId: "btn-demographics-cancel" } : undefined}
            primary={{
              label: "Confirm and Continue",
              onClick: handleConfirm,
              disabled: hasMissing || consentChoice !== "obtained",
              testId: "btn-demographics-confirm",
            }}
            blockedReason={refinedBlockedReason}
          />
        ) : (
        <div className="mt-6 space-y-2">
          <Button
            onClick={handleConfirm}
            size="lg"
            className="w-full"
            disabled={hasMissing || consentChoice !== "obtained"}
          >
            Confirm and Continue
          </Button>
          {onCancel && (
            <Button onClick={onCancel} variant="outline" size="lg" className="w-full">
              Cancel
            </Button>
          )}
        </div>
        )}
      </div>

      <Dialog open={editingField !== null} onOpenChange={() => setEditingField(null)}>
        <DialogContent
          className={isDarkMode ? "bg-gray-800 border-gray-700" : undefined}
        >
          <DialogHeader>
            <DialogTitle>
              {fieldConfigMap.get(editingField as any)?.label}
            </DialogTitle>
          </DialogHeader>

          {editingField && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm">New value</Label>
                {fieldConfigMap.get(editingField as any)?.type === "date" ? (
                  <Input
                    type="date"
                    value={editValue as string}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="mt-1"
                  />
                ) : fieldConfigMap.get(editingField as any)?.type === "sensitive_attribute" ? (
                  <Select
                    value={editValue ? String(editValue) : ""}
                    onValueChange={(v) => setEditValue(v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(
                        fieldConfigMap.get(editingField as any)?.selectOptions || []
                      ).map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : fieldConfigMap.get(editingField as any)?.type === "select" ? (
                  <Select
                    value={editValue ? String(editValue) : ""}
                    onValueChange={(v) => setEditValue(v === "true" ? true : v === "false" ? false : v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(
                        fieldConfigMap.get(editingField as any)?.selectOptions || []
                      ).map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type="text"
                    value={String(editValue || "")}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="mt-1"
                  />
                )}
                {refined && fieldConfigMap.get(editingField as any)?.selectOptions?.find((o) => o.value === String(editValue))?.helpText && (
                  <p className="mt-1 text-xs text-gray-500">
                    {fieldConfigMap.get(editingField as any)?.selectOptions?.find((o) => o.value === String(editValue))?.helpText}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-sm">Reason for amendment</Label>
                <Select value={editReason} onValueChange={(v: any) => setEditReason(v)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {(
                      fieldConfigMap.get(editingField as any)?.amendmentReasons ||
                      Object.keys(DEFAULT_AMENDMENT_REASON_LABELS)
                    ).map((reason: any) => (
                      <SelectItem key={reason} value={reason}>
                        {DEFAULT_AMENDMENT_REASON_LABELS[reason as AmendmentReason]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {editReason === "other" && (
                <div>
                  <Label className="text-sm">Please specify</Label>
                  <Textarea
                    value={editNote}
                    onChange={(e) => setEditNote(e.target.value)}
                    placeholder="Explain the reason..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={handleSaveAmendment}
                  className="flex-1"
                  disabled={!editReason || (editReason === "other" && !editNote)}
                >
                  Save Amendment
                </Button>
                <Button
                  onClick={() => setEditingField(null)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDataSharingPopupOpen}
        onOpenChange={setIsDataSharingPopupOpen}
      >
        <DialogContent
          className={`max-w-2xl ${
            isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white"
          }`}
        >
          <DialogHeader>
            <DialogTitle
              className={`text-lg font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Data Sharing Details
            </DialogTitle>
          </DialogHeader>
          <div
            className={`space-y-4 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            <ul className="space-y-3 list-none">
              {[
                "Consent is required for service provision.",
                "Your information will be shared with NHS England, the NHS BSA, and your registered GP Practice.",
                "Consent may be obtained from the patient or a qualified advocate presenting on their behalf. For patients under 16, consider Fraser/Gillick competency before proceeding.",
              ].map((line) => (
                <li key={line} className="flex gap-3">
                  <span className="text-blue-600 dark:text-blue-400 font-bold flex-shrink-0">
                    •
                  </span>
                  <span className="text-sm">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
